import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Empty, Form, Select, Typography } from 'antd';
import dayjs from 'dayjs';

import type { ModelProductsAcceptance } from '@app/api/Products';
import { CustomInputNumber } from '@app/components';
import {
  PF,
  productsColumns,
} from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/data';
import { useProductOperation } from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/hooks';
import {
  AddProductsModal,
  OperationCommentField,
  OperationDateField,
  OperationFormButtons,
  OperationWarehouseSelect,
  ProductCountInput,
  ProductNameCell,
} from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/ui';
import { toOperationDateTime } from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/utils';
import { divideAmount } from '@app/utils';

import '@pages/Warehouses/WarehouseOperations/components/ProductOperation/styles/ProductOperation.scss';

const { Item, List } = Form;
const { Text } = Typography;
const TABLE = `${PF}_products-table`;

export const GoodsReceipt = () => {
  const {
    form,
    isOpenAddProductsModal,
    openAddProductsModal,
    closeAddProductsModal,
    onAdd,
    isPageLoading,
    warehousesOptions,
    suppliersOptions,
    products,
    selectedProducts,
    formValues,
    onChangeTotalPrice,
    totals,
    acceptProductsHandler,
    onDelete,
    isLoadingAcceptProducts,
  } = useProductOperation();

  return (
    <>
      <Form<ModelProductsAcceptance>
        form={form}
        className="product-operation-form"
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 5 }}
        labelAlign="left"
        disabled={isPageLoading || isLoadingAcceptProducts}
        initialValues={{
          DateAccepted: toOperationDateTime(dayjs()),
          SupplierId: undefined,
          WarehouseId: undefined,
          Comment: '',
          Products: [],
        }}
        onFinish={acceptProductsHandler}
      >
        <OperationDateField name="DateAccepted" />
        <Item name="SupplierId" label="Поставщик" wrapperCol={{ span: 7 }}>
          <Select
            options={suppliersOptions}
            placeholder="Выберите поставщика"
          />
        </Item>
        <OperationWarehouseSelect
          name="WarehouseId"
          label="Склад"
          options={warehousesOptions}
        />

        <div className={`${TABLE}_container`}>
          <List name="Products">
            {(fields, { remove }) => (
              <>
                <div className={`${TABLE}_scroll-wrapper`}>
                  <table className={TABLE}>
                    <thead className={`${TABLE}_thead`}>
                      <tr>
                        {productsColumns.map(({ id, title, className }) => (
                          <th
                            key={id}
                            className={`${TABLE}_th ${TABLE}_${className}`}
                          >
                            {title}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {fields && fields?.length ? (
                        fields.map((field, index) => {
                          const { name, key } = field;
                          const id = form.getFieldValue([
                            'Products',
                            name,
                            'ProductId',
                          ]);
                          const extraProduct = products?.find(
                            (p) => p.Id === id
                          );

                          const price =
                            formValues?.Products?.[name]?.PriceOfPurchase ?? 0;
                          const count =
                            formValues?.Products?.[name]?.Count ?? 1;
                          const total = price * count;

                          return (
                            <tr key={key} className={`${TABLE}_row`}>
                              <td className={`${TABLE}_cell`}>
                                <span className="no-wrap-text">
                                  {index + 1}
                                </span>
                              </td>

                              <td className={`${TABLE}_cell`}>
                                <ProductNameCell
                                  productId={id}
                                  name={`${
                                    extraProduct?.Name ||
                                    extraProduct?.NameShort ||
                                    'Неизвестный товар'
                                  }${
                                    extraProduct?.UnitOfMeasurement
                                      ? `, ${extraProduct.UnitOfMeasurement}`
                                      : ''
                                  }`}
                                />
                              </td>

                              <td className={`${TABLE}_cell`}>
                                <Item
                                  name={[name, 'PriceOfPurchase']}
                                  labelCol={{ span: 0 }}
                                  wrapperCol={{ span: 24 }}
                                  rules={[
                                    { required: true, message: 'Введите цену' },
                                  ]}
                                >
                                  <CustomInputNumber
                                    className="required-field"
                                    allowDecimal={true}
                                    inputValue={form.getFieldValue([
                                      'Products',
                                      name,
                                      'PriceOfPurchase',
                                    ])}
                                    isAllowed={(floatValue: number) =>
                                      floatValue >= 0
                                    }
                                    onSuccess={({ value }) => {
                                      const raw = String(value).replace(
                                        /\s/g,
                                        ''
                                      );
                                      const numeric =
                                        raw === '' ? 0 : Number(raw);
                                      if (!Number.isNaN(numeric)) {
                                        form.setFieldValue(
                                          ['Products', name, 'PriceOfPurchase'],
                                          numeric
                                        );
                                      }
                                    }}
                                  />
                                </Item>
                              </td>

                              <td className={`${TABLE}_cell`}>
                                <ProductCountInput
                                  fieldName={name}
                                  form={form}
                                />
                              </td>

                              <td className={`${TABLE}_cell`}>
                                <Item
                                  labelCol={{ span: 0 }}
                                  wrapperCol={{ span: 24 }}
                                >
                                  <CustomInputNumber
                                    className="required-field"
                                    allowDecimal={true}
                                    allowEmpty={false}
                                    inputValue={total}
                                    isAllowed={(floatValue: number) =>
                                      floatValue >= 0
                                    }
                                    onSuccess={({ value }) => {
                                      const raw = String(value).replace(
                                        /\s/g,
                                        ''
                                      );
                                      if (raw === '') {
                                        onChangeTotalPrice(null, name);
                                        return;
                                      }
                                      const numeric = Number(raw);
                                      if (Number.isNaN(numeric)) return;
                                      onChangeTotalPrice(numeric, name);
                                    }}
                                  />
                                </Item>
                              </td>

                              <td className={`${TABLE}_cell`}>
                                <Item>
                                  <Button
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                      onDelete(id);
                                      remove(name);
                                    }}
                                  />
                                </Item>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={6}>
                            <Empty
                              className={`${TABLE}_empty`}
                              description="Товары не выбраны"
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className={`${TABLE}_footer`}>
                        <td className={`${TABLE}_footer_cell`} colSpan={6}>
                          <div className={`${TABLE}_footer_buttons`}>
                            <Button
                              type="primary"
                              onClick={openAddProductsModal}
                              icon={<PlusOutlined />}
                              className={`${TABLE}_footer_buttons_item`}
                            >
                              Добавить товар
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {fields && fields?.length > 0 && (
                        <tr className={`${TABLE}_footer`}>
                          <td className={`${TABLE}_footer_cell`} />
                          <td className={`${TABLE}_footer_cell`}>
                            <Text strong>Итого:</Text>
                          </td>
                          <td className={`${TABLE}_footer_cell`} />
                          <td className={`${TABLE}_footer_cell`}>
                            <Text strong style={{ padding: 12 }}>
                              {divideAmount(totals.count)}
                            </Text>
                          </td>
                          <td className={`${TABLE}_footer_cell`}>
                            <Text strong style={{ padding: 12 }}>
                              {divideAmount(totals.sum)}
                            </Text>
                          </td>
                        </tr>
                      )}
                    </tfoot>
                  </table>
                </div>
              </>
            )}
          </List>
        </div>

        <OperationCommentField />
        <OperationFormButtons
          submitLabel="Принять"
          isLoading={isLoadingAcceptProducts}
          isSubmitDisabled={!formValues?.Products?.length || isPageLoading}
          onReset={() => form.resetFields()}
        />
      </Form>
      <AddProductsModal
        isOpen={isOpenAddProductsModal}
        onClose={closeAddProductsModal}
        onAdd={onAdd}
        productList={products ?? []}
        suppliersOptions={suppliersOptions}
        warehousesOptions={warehousesOptions}
        selectedProducts={selectedProducts}
      />
    </>
  );
};
